import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  UsersIcon,
  CursorArrowRaysIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { DashboardStats } from '../types';
import { StatCard } from '../components/ui/StatCard';
import { Card, CardHeader, CardBody } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';

export default function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: DashboardStats }>('/reports/dashboard');
      return response.data.data;
    },
  });

  const stats = [
    {
      name: 'Total Affiliates',
      value: data?.totalAffiliates || 0,
      change: data?.activeAffiliates
        ? { value: Math.round((data.activeAffiliates / (data.totalAffiliates || 1)) * 100), type: 'increase' as const }
        : undefined,
      icon: <UsersIcon className="w-6 h-6" />,
      gradient: 'purple' as const,
    },
    {
      name: 'Total Clicks',
      value: (data?.clicks || 0).toLocaleString(),
      icon: <CursorArrowRaysIcon className="w-6 h-6" />,
      gradient: 'cyan' as const,
    },
    {
      name: 'Conversions',
      value: data?.conversions || 0,
      change: data?.conversionRate
        ? { value: data.conversionRate, type: 'increase' as const }
        : undefined,
      icon: <ArrowTrendingUpIcon className="w-6 h-6" />,
      gradient: 'green' as const,
    },
    {
      name: 'Period Commissions',
      value: `$${(data?.periodCommissions || 0).toLocaleString()}`,
      icon: <CurrencyDollarIcon className="w-6 h-6" />,
      gradient: 'purple' as const,
    },
    {
      name: 'Pending Payouts',
      value: `$${(data?.pendingPayouts?.amount || 0).toLocaleString()}`,
      change: data?.pendingPayouts?.count
        ? { value: data.pendingPayouts.count, type: 'decrease' as const }
        : undefined,
      icon: <BanknotesIcon className="w-6 h-6" />,
      gradient: 'orange' as const,
    },
  ];

  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="h-10 w-48 bg-[var(--bg-tertiary)] rounded-xl animate-pulse" />

        {/* Stats skeleton */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-32 bg-[var(--bg-tertiary)] rounded-2xl animate-pulse" />
          ))}
        </div>

        {/* Cards skeleton */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="h-64 bg-[var(--bg-tertiary)] rounded-2xl animate-pulse" />
          <div className="h-64 bg-[var(--bg-tertiary)] rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-display-lg text-gradient">Dashboard</h1>
          <p className="mt-1 text-[var(--text-secondary)]">
            Welcome back! Here's what's happening with your affiliate network.
          </p>
        </div>
        <Button
          variant="primary"
          leftIcon={<ArrowTrendingUpIcon className="w-5 h-5" />}
          onClick={() => window.location.href = '/reports'}
        >
          View Reports
        </Button>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatCard
              title={stat.name}
              value={stat.value}
              change={stat.change}
              icon={stat.icon}
              gradient={stat.gradient}
            />
          </motion.div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Conversions by Type */}
        <Card variant="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <h2 className="text-heading text-[var(--text-primary)]">Conversions by Type</h2>
              <Badge variant="info" dot pulse>Live</Badge>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="flex justify-between items-center p-4 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-color)]"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-primary-500/20">
                    <UsersIcon className="w-5 h-5 text-primary-400" />
                  </div>
                  <span className="text-[var(--text-secondary)]">Players (Gaming)</span>
                </div>
                <span className="text-2xl font-bold text-[var(--text-primary)]">{data?.players || 0}</span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="flex justify-between items-center p-4 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-color)]"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-accent-500/20">
                    <ArrowTrendingUpIcon className="w-5 h-5 text-accent-400" />
                  </div>
                  <span className="text-[var(--text-secondary)]">Traders (Forex)</span>
                </div>
                <span className="text-2xl font-bold text-[var(--text-primary)]">{data?.traders || 0}</span>
              </motion.div>
            </div>
          </CardBody>
        </Card>

        {/* Quick Actions */}
        <Card variant="gradient">
          <CardHeader>
            <h2 className="text-heading text-[var(--text-primary)]">Quick Actions</h2>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Button
                  variant="primary"
                  fullWidth
                  onClick={() => window.location.href = '/affiliates?status=pending'}
                  className="justify-start"
                >
                  <UsersIcon className="w-5 h-5 mr-2" />
                  Review Pending Affiliates
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => window.location.href = '/payouts?status=pending'}
                  className="justify-start"
                >
                  <BanknotesIcon className="w-5 h-5 mr-2" />
                  Process Pending Payouts
                  {data?.pendingPayouts?.count && (
                    <Badge variant="error" className="ml-auto">{data.pendingPayouts.count}</Badge>
                  )}
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => window.location.href = '/commissions'}
                  className="justify-start"
                >
                  <CurrencyDollarIcon className="w-5 h-5 mr-2" />
                  View Commission Periods
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  variant="success"
                  fullWidth
                  onClick={() => window.location.href = '/campaigns'}
                  className="justify-start"
                >
                  <ArrowTrendingUpIcon className="w-5 h-5 mr-2" />
                  Create New Campaign
                </Button>
              </motion.div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Activity Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-heading text-[var(--text-primary)]">Recent Activity</h2>
            <Button variant="ghost" size="sm">View All</Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="text-center py-8 text-[var(--text-muted)]">
            <ArrowTrendingUpIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Activity feed coming soon</p>
            <p className="text-sm mt-1">Track conversions, signups, and payouts in real-time</p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
